import { Activity, Calendar, Mail, Phone, Shield, User } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { getFileUrl } from '@/clients/http'
import { ProfileModel } from '@/types'
import { AdvancedImage } from '@/components/ui'
import { formatLongDate } from '@/utils'

interface ProfileInfoProps {
    profile: ProfileModel.Profile
}

export const ProfileInfo = ({ profile }: ProfileInfoProps) => {


    const InfoItem = ({ icon: Icon, label, value, children }: any) => (
        <div className="space-y-1 py-3">
            <div className="flex items-center gap-2 text-sm text-text-2">
                <Icon className="size-4" />
                {label}
            </div>
            {children || <p className="text-sm text-ink">{value}</p>}
        </div>
    )

    return (
        <Card>
            <CardHeader>
                <CardTitle>Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                    <AdvancedImage width={64} height={64} avatar={true} className="shrink-0" src={getFileUrl(profile?.image?.path)} />
                    <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <p className="truncate text-heading-md text-ink">{profile.name}</p>
                            <Badge tone={profile.status === 'active' ? 'success' : 'soft'} className="capitalize">
                                {profile.status}
                            </Badge>
                        </div>
                        <p className="flex items-center gap-2 truncate text-sm text-text-2">
                            <Mail className="size-4 shrink-0" /> {profile.email}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-x-6 border-t border-border-subtle sm:grid-cols-2">
                    <InfoItem icon={User} label="Full name" value={profile.name} />
                    <InfoItem icon={Phone} label="Phone" value={profile.phone || 'Not provided'} />

                    <InfoItem icon={Shield} label="Roles">
                        <div className="flex flex-wrap gap-1.5 pt-0.5">
                            {profile.roles?.map((role: any) => (
                                <Badge key={role._id} tone="soft">
                                    {role.name}
                                </Badge>
                            ))}
                            {!profile.roles?.length && <p className="text-sm text-text-2">No roles assigned</p>}
                        </div>
                    </InfoItem>

                    <InfoItem icon={Calendar} label="Member since">
                        <p className="text-sm text-ink">
                            {formatLongDate(profile.created_at)}
                        </p>
                    </InfoItem>

                    <InfoItem icon={Activity} label="Last updated">
                        <p className="text-sm text-ink">{formatLongDate(profile.updated_at)}</p>
                    </InfoItem>
                </div>
            </CardContent>
        </Card>
    )
}
